import {useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    TextField,
    Typography,
} from "@mui/material";
import AppModalBox from "./AppModalBox";
import {createCompany} from "../api/api";
import {ApiError} from "../types/api-errors";

type Props = {
    onClose: () => void;
    onSuccess: () => void;
    onError: (error: ApiError | any) => void;
};

const CompanyForm = ({onClose, onSuccess, onError}: Props) => {
    const {t} = useTranslation();

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [metadataSettings, setMetadataSettings] = useState("");
    const [active, setActive] = useState(true);
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            onError(t("companieslist.errors.nameRequired"));
            return;
        }

        try {
            setSaving(true);

            await createCompany({
                name: name.trim(),
                description: description.trim() || null,
                metadataSettings: metadataSettings.trim() || null,
                active,
            });

            onSuccess();
        } catch (error) {
            onError(error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <AppModalBox
            sx={{
                p: 4,
                margin: "auto",
                width: 540,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Typography variant="h6">
                {t("companieslist.createCompany")}
            </Typography>

            <form onSubmit={handleSubmit}>
                <TextField
                    label={t("companieslist.name")}
                    fullWidth
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={saving}
                    sx={{mb: 2}}
                />

                <TextField
                    label={t("companieslist.description")}
                    fullWidth
                    multiline
                    minRows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={saving}
                    sx={{mb: 2}}
                />

                <TextField
                    label={t("companieslist.metadataSettingsJson")}
                    fullWidth
                    multiline
                    minRows={4}
                    value={metadataSettings}
                    onChange={(e) => setMetadataSettings(e.target.value)}
                    disabled={saving}
                    placeholder='{"theme":"default","features":[]}'
                    sx={{mb: 2}}
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={active}
                            onChange={(e) => setActive(e.target.checked)}
                            disabled={saving}
                        />
                    }
                    label={t("companieslist.active")}
                    sx={{mb: 2}}
                />

                <Box sx={{display: "flex", gap: 1, justifyContent: "flex-end"}}>
                    <Button onClick={onClose} disabled={saving}>
                        {t("common.cancel")}
                    </Button>

                    <Button type="submit" variant="contained" disabled={saving}>
                        {saving ? t("companieslist.saving") : t("common.create")}
                    </Button>
                </Box>
            </form>
        </AppModalBox>
    );
};

export default CompanyForm;